import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { OrderedMap } from "immutable";
import i18n from "i18n";
import UserNotification from "util/UserNotification";
import { useDashboardContext } from "security-app/components/Investigations/Dashboard";
import { defaultOnError } from "util/conditional/onError";
import { deserializeInvestigationProcedures } from "security-app/components/Investigations/Details/ProceduresSegment/Procedures/constants";
import { deserializeProceduresStepEvents } from "security-app/components/Investigations/Details/ProceduresSegment/Procedures/ProcedureStepEvents/constants";
import { SLICING_QUERY_KEY, slicesQueryKeyForColumn } from "components/common/PaginatedEntityTable/slicing/queryKeys";
import {
  investigationQueryKey,
  investigationDetailsQueryKey,
  investigationDrawerEventsQueryKey,
  investigationDrawerLogsQueryKey,
  INVESTIGATIONS_QUERY_KEY
} from "security-app/hooks/queryKeys";
import {
  searchInvestigations,
  fetchInvestigations,
  getInvestigationDetails,
  newInvestigation,
  updateInvestigations,
  archiveInvestigation,
  fetchPriorities,
  fetchStatuses,
  fetchUsers,
  fetchTeams,
  addNote,
  deleteNote,
  editNote,
  addEvidence,
  bulkAddEvidence,
  addPriority,
  updatePriority,
  reprioritizePriorities,
  deletePriority,
  addStatus,
  updateStatus,
  deleteStatus,
  removeEvidence,
  bulkArchiveInvestigation,
  deleteInvestigation,
  bulkDeleteInvestigations,
  setDefaultPriority,
  removeDefaultPriority,
  setDefaultStatus,
  removeDefaultStatus,
  enableNotifications,
  fetchInvestigationsConfig,
  fetchInvestigationProcedures,
  fetchProcedureStepEvents,
  fetchProcedureTitles
} from "./api/investigationsAPI";
export function useInvestigationsCount(filters) {
  var _a;
  const { data, isLoading } = useQuery({
    // Nested under the investigations key, so the count refreshes when any investigation mutation invalidates it.
    queryKey: investigationQueryKey("count", filters),
    queryFn: () => defaultOnError(
      searchInvestigations({
        page: 1,
        pageSize: 1,
        query: "",
        sort: { attributeId: "created_at", direction: "desc" },
        filters: OrderedMap(filters)
      }),
      i18n.t("notifications.fetch_investigations_count_failed", { ns: "enterprise_security_investigations" })
    )
  });
  return { count: (_a = data == null ? void 0 : data.pagination.total) != null ? _a : 0, loadingCount: isLoading };
}
const ASSOCIATED_INVESTIGATIONS_SLICE_COLUMN = "associated_investigations";
const invalidateInvestigationsTableQueries = (queryClient) => {
  queryClient.invalidateQueries({ queryKey: [INVESTIGATIONS_QUERY_KEY] });
  queryClient.invalidateQueries({ queryKey: [SLICING_QUERY_KEY] });
};
export function useGetInvestigations({ page, perPage, query = void 0, orderBy = void 0, direction = void 0, filters = void 0 }, archived, userTimezone, options) {
  var _a;
  const { data, isLoading } = useQuery({
    queryKey: ["get-investigations", page, perPage, query, orderBy, direction, filters, userTimezone, archived],
    queryFn: () => defaultOnError(
      fetchInvestigations(page, perPage, query, orderBy, direction, filters, archived, userTimezone),
      i18n.t("notifications.fetch_investigations_failed", { ns: "enterprise_security_investigations" })
    ),
    retry: 2,
    placeholderData: keepPreviousData,
    enabled: (_a = options == null ? void 0 : options.enabled) != null ? _a : true
  });
  return {
    loadingInvestigations: isLoading,
    investigations: isLoading ? [] : data == null ? void 0 : data.investigations,
    pagination: {
      page: (data == null ? void 0 : data.page) || page,
      perPage: (data == null ? void 0 : data.per_page) || perPage,
      total: (data == null ? void 0 : data.total) || 0,
      grandTotal: (data == null ? void 0 : data.grand_total) || 0,
      count: (data == null ? void 0 : data.count) || 0
    }
  };
}
export function useGetInvestigationDetails(invId, loadCompleteLogAndEvents = false) {
  const { data, isLoading, isError } = useQuery({
    queryKey: investigationDetailsQueryKey(invId, loadCompleteLogAndEvents),
    queryFn: () => {
      var _a;
      try {
        return getInvestigationDetails(invId, loadCompleteLogAndEvents);
      } catch (error) {
        const currentInvestigationId = (_a = localStorage.getItem("currentInvestigationId")) != null ? _a : void 0;
        if (currentInvestigationId === invId) {
          UserNotification.warning(
            i18n.t("notifications.investigation_deleted_while_viewing", { ns: "enterprise_security_investigations" })
          );
        } else if (error.status === 404) {
          UserNotification.error(
            i18n.t("notifications.unable_to_find_investigation", {
              ns: "enterprise_security_investigations",
              id: invId
            }),
            i18n.t("notifications.could_not_retrieve_investigation_title", {
              ns: "enterprise_security_investigations"
            })
          );
        } else {
          UserNotification.error(error.message);
        }
        throw error;
      }
    },
    retry: 2,
    enabled: !!invId
  });
  return {
    loadingInvestigationDetails: isLoading,
    fetchError: isError,
    currentInvestigation: data
  };
}
export function useNewInvestigation() {
  const queryClient = useQueryClient();
  const { forceRefreshDashboard } = useDashboardContext();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: newInvestigation,
    onSuccess: () => {
      UserNotification.success(
        i18n.t("notifications.investigation_created", { ns: "enterprise_security_investigations" })
      );
      invalidateInvestigationsTableQueries(queryClient);
      forceRefreshDashboard();
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return { createInvestigation: mutateAsync, creatingInvestigation: isLoading };
}
export function useUpdateInvestigations() {
  const queryClient = useQueryClient();
  const { forceRefreshDashboard } = useDashboardContext();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: updateInvestigations,
    onSuccess: (data) => {
      if (data.errors.length > 0) {
        const error = { type: "warning", message: `${data.errors}` };
        UserNotification[error.type](error.message);
      } else {
        UserNotification.success(
          i18n.t("notifications.investigations_updated", { ns: "enterprise_security_investigations" })
        );
      }
      queryClient.invalidateQueries({
        queryKey: investigationDetailsQueryKey()
      });
      invalidateInvestigationsTableQueries(queryClient);
      forceRefreshDashboard();
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return {
    updateInvestigations: mutateAsync,
    updatingInvestigations: isLoading
  };
}
export function useArchiveInvestigation() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: archiveInvestigation,
    onSuccess: () => {
      UserNotification.success(
        i18n.t("notifications.investigation_archive_status_updated", { ns: "enterprise_security_investigations" })
      );
      queryClient.invalidateQueries({
        queryKey: investigationDetailsQueryKey()
      });
      invalidateInvestigationsTableQueries(queryClient);
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return {
    archiveInvestigation: mutateAsync,
    archivingInvestigation: isLoading
  };
}
export function useBulkArchiveInvestigation() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: bulkArchiveInvestigation,
    onSuccess: () => {
      UserNotification.success(
        i18n.t("notifications.investigations_archive_status_updated", { ns: "enterprise_security_investigations" })
      );
      queryClient.invalidateQueries({
        queryKey: investigationDetailsQueryKey()
      });
      invalidateInvestigationsTableQueries(queryClient);
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return {
    bulkArchiveInvestigation: mutateAsync,
    archivingInvestigations: isLoading
  };
}
export function useDeleteInvestigation() {
  const queryClient = useQueryClient();
  const { forceRefreshDashboard } = useDashboardContext();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: deleteInvestigation,
    onSuccess: () => {
      UserNotification.success(
        i18n.t("notifications.investigation_deleted", { ns: "enterprise_security_investigations" })
      );
      invalidateInvestigationsTableQueries(queryClient);
      forceRefreshDashboard();
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return {
    deleteInvestigation: mutateAsync,
    deletingInvestigation: isLoading
  };
}
export function useBulkDeleteInvestigations() {
  const queryClient = useQueryClient();
  const { forceRefreshDashboard } = useDashboardContext();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: bulkDeleteInvestigations,
    onSuccess: () => {
      UserNotification.success(
        i18n.t("notifications.investigations_deleted", { ns: "enterprise_security_investigations" })
      );
      invalidateInvestigationsTableQueries(queryClient);
      forceRefreshDashboard();
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return {
    bulkDelete: mutateAsync,
    bulkDeleting: isLoading
  };
}
export function useAddEvidence() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: addEvidence,
    onSuccess: (data, variables) => {
      var _a, _b;
      if (data.errors.length > 0) {
        const error = { type: "warning", message: `${data.errors}` };
        UserNotification[error.type](error.message);
      } else {
        UserNotification.success(i18n.t("notifications.evidence_added", { ns: "enterprise_security_investigations" }));
      }
      queryClient.invalidateQueries({
        queryKey: investigationDetailsQueryKey()
      });
      if ((_a = variables.payload.events) == null ? void 0 : _a.length) {
        queryClient.invalidateQueries({
          queryKey: investigationDrawerEventsQueryKey(variables.invId)
        });
      }
      if ((_b = variables.payload.logs) == null ? void 0 : _b.length) {
        queryClient.invalidateQueries({
          queryKey: investigationDrawerLogsQueryKey(variables.invId)
        });
      }
      queryClient.invalidateQueries({
        queryKey: slicesQueryKeyForColumn(ASSOCIATED_INVESTIGATIONS_SLICE_COLUMN)
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return { addEvidence: mutateAsync, addingEvidence: isLoading };
}
const invalidateEntities = (queryClient, payload) => {
  const investigations = payload == null ? void 0 : payload.investigation_ids;
  const events = payload == null ? void 0 : payload.events;
  const logs = payload == null ? void 0 : payload.logs;
  if (investigations == null ? void 0 : investigations.length) {
    investigations.forEach((investigation) => {
      queryClient.invalidateQueries({
        queryKey: investigationDetailsQueryKey(investigation)
      });
      if (events == null ? void 0 : events.length) {
        queryClient.invalidateQueries({
          queryKey: investigationDrawerEventsQueryKey(investigation)
        });
      }
      if (logs == null ? void 0 : logs.length) {
        queryClient.invalidateQueries({
          queryKey: investigationDrawerLogsQueryKey(investigation)
        });
      }
    });
  }
  if (events == null ? void 0 : events.length) {
    events.forEach(
      (event) => queryClient.invalidateQueries({
        queryKey: ["get-security-events", event]
      })
    );
  }
  queryClient.invalidateQueries({
    queryKey: slicesQueryKeyForColumn(ASSOCIATED_INVESTIGATIONS_SLICE_COLUMN)
  });
};
export function useBulkAddEvidence() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: bulkAddEvidence,
    onSuccess: (data, variables) => {
      if (data.errors.length > 0) {
        const error = { type: "warning", message: `${data.errors}` };
        UserNotification[error.type](error.message);
      } else {
        UserNotification.success(i18n.t("notifications.evidence_added", { ns: "enterprise_security_investigations" }));
      }
      invalidateEntities(queryClient, variables.payload);
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return { bulkAddEvidence: mutateAsync, addingEvidence: isLoading };
}
export function useRemoveEvidence() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: removeEvidence,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: investigationDrawerEventsQueryKey(variables.invId)
      });
      queryClient.invalidateQueries({
        queryKey: investigationDrawerLogsQueryKey(variables.invId)
      });
      UserNotification.success(i18n.t("notifications.evidence_removed", { ns: "enterprise_security_investigations" }));
      queryClient.invalidateQueries({
        queryKey: investigationDetailsQueryKey()
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return { removeEvidence: mutateAsync, removingEvidence: isLoading };
}
export function useAddNote() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: addNote,
    onSuccess: () => {
      UserNotification.success(i18n.t("notifications.note_added", { ns: "enterprise_security_investigations" }));
      queryClient.invalidateQueries({
        queryKey: investigationDetailsQueryKey()
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return { addNote: mutateAsync, addingNote: isLoading };
}
export function useEditNote() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: editNote,
    onSuccess: () => {
      UserNotification.success(i18n.t("notifications.note_edited", { ns: "enterprise_security_investigations" }));
      queryClient.invalidateQueries({
        queryKey: investigationDetailsQueryKey()
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return { editNote: mutateAsync, editingNote: isLoading };
}
export function useDeleteNote() {
  const queryClient = useQueryClient();
  const { mutateAsync } = useMutation({
    mutationFn: deleteNote,
    onSuccess: () => {
      UserNotification.success(i18n.t("notifications.note_deleted", { ns: "enterprise_security_investigations" }));
      queryClient.invalidateQueries({
        queryKey: investigationDetailsQueryKey()
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return { deleteNote: mutateAsync };
}
export function useGetPriorities(execute = true) {
  const { data, isLoading } = useQuery({
    queryKey: ["get-investigation-priorities"],
    queryFn: () => defaultOnError(
      fetchPriorities(),
      i18n.t("notifications.fetch_priorities_failed", { ns: "enterprise_security_investigations" })
    ),
    retry: 2,
    enabled: execute
  });
  return {
    priorities: (data || []).sort((a, b) => a.priority - b.priority),
    loadingPriorities: isLoading
  };
}
export function useGetStatuses(execute = true) {
  const { data, isLoading } = useQuery({
    queryKey: ["get-all-statuses"],
    queryFn: () => defaultOnError(
      fetchStatuses(),
      i18n.t("notifications.fetch_statuses_failed", { ns: "enterprise_security_investigations" })
    ),
    retry: 2,
    enabled: execute
  });
  return {
    statuses: data || [],
    loadingStatuses: isLoading
  };
}
export function useGetUsers(execute = true) {
  const { data, isLoading } = useQuery({
    queryKey: ["get-all-users"],
    queryFn: () => defaultOnError(
      fetchUsers(),
      i18n.t("notifications.fetch_users_failed", { ns: "enterprise_security_investigations" })
    ),
    retry: 2,
    enabled: execute
  });
  const { users } = data || { users: [] };
  return {
    users,
    loadingUsers: isLoading
  };
}
export function useGetTeams(execute = true) {
  const { data, isLoading } = useQuery({
    queryKey: ["get-all-teams"],
    queryFn: () => defaultOnError(
      fetchTeams(),
      i18n.t("notifications.fetch_teams_failed", { ns: "enterprise_security_investigations" })
    ),
    retry: 2,
    enabled: execute
  });
  const { teams } = data || { teams: [] };
  return {
    teams,
    loadingTeams: isLoading
  };
}
export function useAddPriority() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: addPriority,
    onSuccess: () => {
      UserNotification.success(
        i18n.t("notifications.priority_tag_added", { ns: "enterprise_security_investigations" })
      );
      queryClient.invalidateQueries({
        queryKey: ["get-investigation-priorities"]
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return { addPriority: mutateAsync, addingPriority: isLoading };
}
export function useUpdatePriority() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: updatePriority,
    onSuccess: () => {
      UserNotification.success(
        i18n.t("notifications.priority_tag_updated", { ns: "enterprise_security_investigations" })
      );
      queryClient.invalidateQueries({
        queryKey: ["get-investigation-priorities"]
      });
      invalidateInvestigationsTableQueries(queryClient);
      queryClient.invalidateQueries({
        queryKey: investigationDetailsQueryKey()
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return { updatePriority: mutateAsync, updatingPriority: isLoading };
}
export function useReprioritizePriorities() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: reprioritizePriorities,
    onSuccess: () => {
      UserNotification.success(
        i18n.t("notifications.priority_tags_updated", { ns: "enterprise_security_investigations" })
      );
      queryClient.invalidateQueries({
        queryKey: ["get-investigation-priorities"]
      });
      invalidateInvestigationsTableQueries(queryClient);
      queryClient.invalidateQueries({
        queryKey: investigationDetailsQueryKey()
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return { reprioritizePriorities: mutateAsync, reprioritizingPriorities: isLoading };
}
export function useDeletePriority() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: deletePriority,
    onSuccess: () => {
      UserNotification.success(
        i18n.t("notifications.priority_tag_deleted", { ns: "enterprise_security_investigations" })
      );
      queryClient.invalidateQueries({
        queryKey: ["get-investigation-priorities"]
      });
      invalidateInvestigationsTableQueries(queryClient);
      queryClient.invalidateQueries({
        queryKey: investigationDetailsQueryKey()
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return { deletePriority: mutateAsync, deletingPriority: isLoading };
}
export function useAddStatus() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: addStatus,
    onSuccess: () => {
      UserNotification.success(i18n.t("notifications.status_tag_added", { ns: "enterprise_security_investigations" }));
      queryClient.invalidateQueries({
        queryKey: ["get-all-statuses"]
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return { addStatus: mutateAsync, addingStatus: isLoading };
}
export function useUpdateStatus() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: updateStatus,
    onSuccess: () => {
      UserNotification.success(
        i18n.t("notifications.status_tag_updated", { ns: "enterprise_security_investigations" })
      );
      queryClient.invalidateQueries({
        queryKey: ["get-all-statuses"]
      });
      invalidateInvestigationsTableQueries(queryClient);
      queryClient.invalidateQueries({
        queryKey: investigationDetailsQueryKey()
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return { updateStatus: mutateAsync, updatingStatus: isLoading };
}
export function useDeleteStatus() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: deleteStatus,
    onSuccess: () => {
      UserNotification.success(
        i18n.t("notifications.status_tag_deleted", { ns: "enterprise_security_investigations" })
      );
      queryClient.invalidateQueries({
        queryKey: ["get-all-statuses"]
      });
      invalidateInvestigationsTableQueries(queryClient);
      queryClient.invalidateQueries({
        queryKey: investigationDetailsQueryKey()
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return { deleteStatus: mutateAsync, deletingStatus: isLoading };
}
export function useSetDefaultPriority() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: setDefaultPriority,
    onSuccess: () => {
      UserNotification.success(
        i18n.t("notifications.default_priority_set", { ns: "enterprise_security_investigations" })
      );
      queryClient.invalidateQueries({
        queryKey: ["get-investigation-priorities"]
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return { setDefault: mutateAsync, settingDefault: isLoading };
}
export function useRemoveDefaultPriority() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: removeDefaultPriority,
    onSuccess: () => {
      UserNotification.success(
        i18n.t("notifications.default_priority_removed", { ns: "enterprise_security_investigations" })
      );
      queryClient.invalidateQueries({
        queryKey: ["get-investigation-priorities"]
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return { removeDefault: mutateAsync, removingDefault: isLoading };
}
export function useSetDefaultStatus() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: setDefaultStatus,
    onSuccess: () => {
      UserNotification.success(
        i18n.t("notifications.default_status_set", { ns: "enterprise_security_investigations" })
      );
      queryClient.invalidateQueries({
        queryKey: ["get-all-statuses"]
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return { setDefault: mutateAsync, settingDefault: isLoading };
}
export function useRemoveDefaultStatus() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: removeDefaultStatus,
    onSuccess: () => {
      UserNotification.success(
        i18n.t("notifications.default_status_removed", { ns: "enterprise_security_investigations" })
      );
      queryClient.invalidateQueries({
        queryKey: ["get-all-statuses"]
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return { removeDefault: mutateAsync, removingDefault: isLoading };
}
export function useEnableNotifications() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: enableNotifications,
    onSuccess: (response) => {
      UserNotification.success(
        response.enabled ? i18n.t("notifications.investigation_notifications_enabled", { ns: "enterprise_security_investigations" }) : i18n.t("notifications.investigation_notifications_disabled", { ns: "enterprise_security_investigations" })
      );
      queryClient.invalidateQueries({
        queryKey: ["get-investigations-config"]
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return { enableNotifications: mutateAsync, enablingNotifications: isLoading };
}
export function useGetInvestigationsConfig() {
  const { data, isLoading } = useQuery({
    queryKey: ["get-investigations-config"],
    queryFn: () => defaultOnError(
      fetchInvestigationsConfig(),
      i18n.t("notifications.fetch_investigations_config_failed", { ns: "enterprise_security_investigations" })
    ),
    retry: 2
  });
  return {
    config: data || {
      enable_assignment_notifications: false,
      default_priority: "",
      default_status: ""
    },
    loadingConfig: isLoading
  };
}
export const investigationProceduresQueryKeyFn = (searchParams = void 0) => searchParams ? ["investigation-procedures", "list", searchParams] : ["investigation-procedures", "list"];
export function useGetInvestigationProcedures(invId) {
  const getInvestigationProcedures = async (searchParams) => {
    const {
      page,
      pageSize,
      query,
      sort: { attributeId, direction }
    } = searchParams;
    const params = [`page=${page}`, `per_page=${pageSize}`];
    if (query) params.push(`query=${encodeURI(query)}`);
    if (attributeId) {
      params.push(`sort=${attributeId}`);
    }
    if (direction) {
      params.push(`direction=${direction}`);
    }
    return fetchInvestigationProcedures(invId, params.join("&")).then(deserializeInvestigationProcedures);
  };
  return { getInvestigationProcedures, investigationProceduresQueryKeyFn };
}
export function useGetProcedureTitles(invId, procedureId, { page, perPage }) {
  const params = [`page=${page}`, `per_page=${perPage}`];
  const { data, isLoading } = useQuery({
    queryKey: ["procedure-titles", invId, procedureId, page, perPage],
    queryFn: () => defaultOnError(
      fetchProcedureTitles(invId, procedureId, params.join("&")),
      i18n.t("notifications.fetch_investigations_failed", { ns: "enterprise_security_investigations" })
    ),
    retry: 2,
    placeholderData: keepPreviousData
  });
  return {
    loadingProcedureTitles: isLoading,
    titles: isLoading ? [] : data == null ? void 0 : data.procedures,
    pagination: {
      page: (data == null ? void 0 : data.page) || page,
      perPage: (data == null ? void 0 : data.per_page) || perPage,
      total: (data == null ? void 0 : data.total) || 0,
      grandTotal: (data == null ? void 0 : data.grand_total) || 0,
      count: (data == null ? void 0 : data.count) || 0
    }
  };
}
export function useGetProcedureStepEvents(invId, procedureId, stepId, isOpened) {
  const stepProcedureEventsQueryKeyFn = (searchParams = void 0) => {
    const queryKey = ["investigation-procedures-step-events", "list", isOpened];
    if (procedureId) queryKey.push(procedureId);
    if (stepId) queryKey.push(stepId);
    if (searchParams) queryKey.push(searchParams);
    return queryKey;
  };
  const getProcedureStepEvents = async (searchParams) => {
    const {
      page,
      pageSize,
      query,
      sort: { attributeId, direction }
    } = searchParams;
    const params = [`page=${page}`, `per_page=${pageSize}`];
    if (query) params.push(`query=${encodeURI(query)}`);
    if (attributeId) {
      params.push(`sort=${attributeId}`);
    }
    if (direction) {
      params.push(`direction=${direction}`);
    }
    return isOpened ? fetchProcedureStepEvents(invId, procedureId, stepId, params.join("&")).then(deserializeProceduresStepEvents) : Promise.resolve({ attributes: [], list: [], pagination: { total: 0, page: 1, per_page: pageSize, count: 0 } });
  };
  return { getProcedureStepEvents, stepProcedureEventsQueryKeyFn };
}
